import * as THREE from 'three'
import Experience from '../Experience.js'
import vertexShader from '../../shaders/particles/vertex.glsl'
import fragmentShader from '../../shaders/particles/fragment.glsl'

const N         = 5000
const PHI       = Math.PI * (3 - Math.sqrt(5))
const TWO_PI    = Math.PI * 2
const MORPH_SPEED = 1 / 80  // ~1.3s at 60fps

const PRESETS = {
    neon:    { a: [0.00, 0.30, 0.70], b: [0.00, 0.90, 1.00] },
    bloom:   { a: [0.35, 0.00, 0.00], b: [0.95, 0.15, 0.05] },
    void:    { a: [0.15, 0.05, 0.35], b: [0.60, 0.20, 0.95] },
    organic: { a: [0.05, 0.30, 0.10], b: [0.20, 0.65, 0.40] },
    minimal: { a: [0.20, 0.25, 0.30], b: [0.60, 0.70, 0.75] },
}

// ── Shape generators ─────────────────────────────────────────────────────────

function genSphere(n) {
    const pos = new Float32Array(n * 3)
    for (let i = 0; i < n; i++) {
        const y     = 1 - (i / (n - 1)) * 2
        const r     = Math.sqrt(Math.max(0, 1 - y * y))
        const theta = PHI * i
        pos[i*3]   = Math.cos(theta) * r
        pos[i*3+1] = y
        pos[i*3+2] = Math.sin(theta) * r
    }
    return pos
}

function genCube(n) {
    const pos = new Float32Array(n * 3)
    const s = 0.8
    for (let i = 0; i < n; i++) {
        const face = Math.floor(Math.random() * 6)
        const u = (Math.random() * 2 - 1) * s
        const v = (Math.random() * 2 - 1) * s
        if      (face === 0) { pos[i*3] =  s; pos[i*3+1] = u; pos[i*3+2] = v }
        else if (face === 1) { pos[i*3] = -s; pos[i*3+1] = u; pos[i*3+2] = v }
        else if (face === 2) { pos[i*3] = u;  pos[i*3+1] =  s; pos[i*3+2] = v }
        else if (face === 3) { pos[i*3] = u;  pos[i*3+1] = -s; pos[i*3+2] = v }
        else if (face === 4) { pos[i*3] = u;  pos[i*3+1] = v; pos[i*3+2] =  s }
        else                 { pos[i*3] = u;  pos[i*3+1] = v; pos[i*3+2] = -s }
    }
    return pos
}

function genTorus(n) {
    const pos = new Float32Array(n * 3)
    const R = 0.7, r = 0.28
    for (let i = 0; i < n; i++) {
        const u  = Math.random() * TWO_PI
        const v  = Math.random() * TWO_PI
        const rc = R + r * Math.cos(v)
        // rotated 90° around X so hole faces camera (+Z)
        pos[i*3]   =  rc * Math.cos(u)
        pos[i*3+1] = -rc * Math.sin(u)
        pos[i*3+2] =  r  * Math.sin(v)
    }
    return pos
}

function genIcosahedron(n) {
    const pos  = new Float32Array(n * 3)
    const geo  = new THREE.IcosahedronGeometry(1, 0)
    const verts = geo.attributes.position.array
    const faceCount = Math.floor(verts.length / 9)
    for (let i = 0; i < n; i++) {
        const f = Math.floor(Math.random() * faceCount) * 9
        let r1 = Math.random(), r2 = Math.random()
        if (r1 + r2 > 1) { r1 = 1 - r1; r2 = 1 - r2 }
        const r3 = 1 - r1 - r2
        pos[i*3]   = r1*verts[f]   + r2*verts[f+3] + r3*verts[f+6]
        pos[i*3+1] = r1*verts[f+1] + r2*verts[f+4] + r3*verts[f+7]
        pos[i*3+2] = r1*verts[f+2] + r2*verts[f+5] + r3*verts[f+8]
    }
    geo.dispose()
    return pos
}

function genTorusKnot(n) {
    const pos   = new Float32Array(n * 3)
    const R = 0.65, r = 0.25, tubeR = 0.10, P = 2, Q = 3

    function curve(t) {
        const rc = R + r * Math.cos(Q * t)
        return [rc * Math.cos(P * t), r * Math.sin(Q * t), rc * Math.sin(P * t)]
    }

    for (let i = 0; i < n; i++) {
        const t  = (i / n) * TWO_PI + (Math.random() - 0.5) * (TWO_PI / n)
        const c  = curve(t)
        const cn = curve(t + 0.01)

        // tangent
        let tx = cn[0]-c[0], ty = cn[1]-c[1], tz = cn[2]-c[2]
        const tl = Math.sqrt(tx*tx + ty*ty + tz*tz)
        tx /= tl; ty /= tl; tz /= tl

        // normal = cross(tangent, up) — switch up if tangent ≈ vertical
        const upX = Math.abs(ty) < 0.99 ? 0 : 1
        const upY = Math.abs(ty) < 0.99 ? 1 : 0
        let nx = ty*0 - tz*upY, ny = tz*upX - tx*0, nz = tx*upY - ty*upX
        const nl = Math.sqrt(nx*nx + ny*ny + nz*nz)
        nx /= nl; ny /= nl; nz /= nl

        // binormal = cross(tangent, normal)
        const bx = ty*nz - tz*ny
        const by = tz*nx - tx*nz
        const bz = tx*ny - ty*nx

        const phi = Math.random() * TWO_PI
        const cp = Math.cos(phi), sp = Math.sin(phi)
        pos[i*3]   = c[0] + tubeR * (cp*nx + sp*bx)
        pos[i*3+1] = c[1] + tubeR * (cp*ny + sp*by)
        pos[i*3+2] = c[2] + tubeR * (cp*nz + sp*bz)
    }
    return pos
}

// ── Class ─────────────────────────────────────────────────────────────────────

export default class ParticleSystem {
    constructor() {
        this.experience = new Experience()
        this.scene      = this.experience.scene
        this.time       = this.experience.time
        this.renderer   = this.experience.renderer.instance
        this.camera     = this.experience.camera.instance

        this._activeTarget  = 1
        this._active        = 0
        this._currentShape  = 0
        this._morphTarget   = 0
        this._morphProgress = 0
        this._morphing      = false

        this._buildGeometry()
        this._buildMaterial('neon')
        this._buildPoints()
    }

    prewarm() {
        const renderer = this.experience.renderer?.instance
        const camera   = this.experience.camera?.instance
        if (renderer && camera && this.points) {
            try { renderer.compile(this.scene, camera) } catch (e) {}
        }
    }

    _buildGeometry() {
        const sphere = genSphere(N)
        const randoms = new Float32Array(N)
        for (let i = 0; i < N; i++) randoms[i] = Math.random()

        this.geometry = new THREE.BufferGeometry()
        this.geometry.setAttribute('position',  new THREE.BufferAttribute(sphere,        3))
        this.geometry.setAttribute('aRandom',   new THREE.BufferAttribute(randoms,       1))
        this.geometry.setAttribute('aPosCube',  new THREE.BufferAttribute(genCube(N),    3))
        this.geometry.setAttribute('aPosTorus', new THREE.BufferAttribute(genTorus(N),   3))
        this.geometry.setAttribute('aPosIco',   new THREE.BufferAttribute(genIcosahedron(N), 3))
        this.geometry.setAttribute('aPosTknot', new THREE.BufferAttribute(genTorusKnot(N),  3))
    }

    _buildMaterial(presetName) {
        const p = PRESETS[presetName] || PRESETS.neon
        this.material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                uTime:    { value: 0 },
                uBass:    { value: 0 },
                uMid:     { value: 0 },
                uHigh:    { value: 0 },
                uSize:    { value: 24 },
                uActive:  { value: 0 },
                uColorA:  { value: new THREE.Color().setRGB(...p.a) },
                uColorB:  { value: new THREE.Color().setRGB(...p.b) },
                uShapeA:  { value: 0.0 },
                uShapeB:  { value: 0.0 },
                uMorphT:  { value: 0.0 },
            },
            transparent: true,
            depthWrite:  false,
            blending:    THREE.AdditiveBlending,
        })
    }

    _buildPoints() {
        this.points = new THREE.Points(this.geometry, this.material)
        this.points.scale.setScalar(0.3)
        this.scene.add(this.points)
    }

    setPreset(name) {
        const p = PRESETS[name] || PRESETS.neon
        this.material.uniforms.uColorA.value.setRGB(...p.a)
        this.material.uniforms.uColorB.value.setRGB(...p.b)
    }

    activate() {
        this._activeTarget = 1
    }

    morphTo(targetIdx) {
        if (targetIdx === this._currentShape && !this._morphing) return
        if (this._morphing) this._snapMorph()

        const u = this.material.uniforms
        u.uShapeA.value  = this._currentShape
        u.uShapeB.value  = targetIdx
        u.uMorphT.value  = 0
        this._morphTarget   = targetIdx
        this._morphing      = true
        this._morphProgress = 0
    }

    _snapMorph() {
        const u = this.material.uniforms
        this._currentShape  = this._morphTarget
        u.uShapeA.value     = this._morphTarget
        u.uMorphT.value     = 0
        this._morphing      = false
        this._morphProgress = 0
    }

    update() {
        const audio = this.experience.world.audioAnalyser
        const u     = this.material.uniforms

        u.uTime.value = this.time.elapsed * 0.001

        if (audio) {
            u.uBass.value = audio.bass
            u.uMid.value  = audio.mid
            u.uHigh.value = audio.high
        }

        this._active += (this._activeTarget - this._active) * 0.015
        u.uActive.value = this._active

        if (this._morphing) {
            this._morphProgress = Math.min(1, this._morphProgress + MORPH_SPEED)
            u.uMorphT.value = this._morphProgress
            if (this._morphProgress >= 1) {
                this._currentShape  = this._morphTarget
                u.uShapeA.value     = this._morphTarget
                u.uMorphT.value     = 0
                this._morphing      = false
                this._morphProgress = 0
            }
        }

        const bassBoost = audio ? audio.bass * 0.003 : 0
        this.points.rotation.y += 0.0004 + bassBoost
    }

    destroy() {
        this.scene.remove(this.points)
        this.geometry.dispose()
        this.material.dispose()
    }
}
